/**
 * Shared Wishlist — public, read-only view of another user's saved shelf.
 *
 * Route: /wishlist/shared/:token
 *
 * Anyone with the link can browse. No heart / no compare toggles — this is
 * strictly a "look at my picks" surface. Includes a "Start your own shelf"
 * CTA so visitors can convert into signed-in users.
 */
import React from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { Heart, ShoppingBag, Sparkles, ArrowRight, ExternalLink } from "lucide-react";
import { API_BASE } from "@/lib/api";
import { ShopShell } from "@/pages/Shop";
import { MiniScoreArc } from "@/components/shared/MiniScoreArc";
import { productImage } from "@/lib/productImage";
import type { Product } from "@/lib/types";

const http = axios.create({ baseURL: API_BASE });

interface SharedResp {
  owner_name: string;
  items: Product[];
  total: number;
  created_at: string;
}

const _tier = (s?: number | null) =>
  s == null ? { label: "", color: "bg-slate-300 text-slate-700" }
    : s >= 800 ? { label: "Trusted", color: "bg-emerald-500 text-white" }
    : s >= 600 ? { label: "Verified", color: "bg-orange-500 text-white" }
    : s >= 400 ? { label: "Emerging", color: "bg-amber-500 text-white" }
    : { label: "Watch", color: "bg-rose-500 text-white" };

export const SharedWishlistPage: React.FC = () => {
  const { token = "" } = useParams<{ token: string }>();
  const q = useQuery<SharedResp>({
    queryKey: ["wishlist-shared", token],
    queryFn: async () => (await http.get(`/shop/wishlist/shared/${token}`)).data,
    retry: false,
  });

  if (q.isError) {
    return (
      <ShopShell title="Wishlist not found — SociBaba" hideFooter showBottomNav>
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-12 text-center" data-testid="shared-wishlist-404">
          <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-rose-500/10 mb-4">
            <Sparkles className="h-6 w-6 text-rose-500" />
          </div>
          <h1 className="font-serif-display font-bold text-2xl text-slate-900 dark:text-white">
            This wishlist is no longer available.
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">
            The link may have been rotated or revoked by its owner.
          </p>
          <Link to="/shop" className="mt-5 inline-flex items-center gap-1.5 bg-orange-500 hover:bg-orange-600 text-white text-sm font-display font-semibold px-5 py-2.5">
            <ShoppingBag className="h-4 w-4" /> Explore the shop
          </Link>
        </div>
      </ShopShell>
    );
  }

  const owner = q.data?.owner_name || "A SociBaba member";
  const items = q.data?.items || [];

  return (
    <ShopShell
      title={`${owner}'s wishlist — SociBaba`}
      description={`${items.length} SIBA-scored products hearted by ${owner}.`}
      hideFooter
      showBottomNav
    >
      {/* Owner strip */}
      <div className="mb-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-orange-500 font-display font-bold">
          <Heart className="h-3 w-3 fill-orange-500" />
          Shared shelf
        </div>
        <h1
          className="font-serif-display font-bold text-2xl sm:text-3xl text-slate-900 dark:text-white tracking-tight mt-1"
          data-testid="shared-wishlist-title"
        >
          {owner}'s picks · <span className="text-orange-500">{items.length}</span> product{items.length === 1 ? "" : "s"}
        </h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 max-w-xl">
          Every card below has been vetted by our SibaScore engine across 11 trust signals. Tap any product to see its full breakdown.
        </p>
      </div>

      {q.isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="bg-slate-100 dark:bg-slate-800 animate-pulse border border-slate-200 dark:border-slate-800 aspect-[3/4]" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-12 text-center">
          <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-orange-500/10 mb-4">
            <Heart className="h-6 w-6 text-orange-500" />
          </div>
          <h2 className="font-serif-display font-bold text-xl text-slate-900 dark:text-white">
            {owner} hasn't saved anything yet.
          </h2>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="shared-wishlist-grid">
          {items.map((p, i) => {
            const t = _tier(p.product_score ?? null);
            return (
              <Link
                key={p.product_id}
                to={`/shop/p/${p.slug}`}
                data-testid={`shared-wishlist-card-${p.slug}`}
                className="group bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 hover:border-slate-900 dark:hover:border-slate-100 transition-colors overflow-hidden flex flex-col"
              >
                <div className="aspect-square bg-slate-50 dark:bg-slate-800 relative overflow-hidden">
                  <img src={productImage(p as any, 600)} alt={p.title} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]" />
                  {t.label && (
                    <span className={`absolute top-0 right-0 ${t.color} text-[10px] font-bold px-2 py-1 font-display uppercase tracking-wider`}>{t.label}</span>
                  )}
                </div>
                <div className="p-4 flex-1 flex flex-col border-t border-slate-100 dark:border-slate-800">
                  <div className="text-[10px] text-slate-400 dark:text-slate-500 uppercase tracking-wider font-display font-semibold">{p.brand || "Unbranded"}</div>
                  <div className="font-display font-semibold text-sm mt-1 line-clamp-2 group-hover:text-orange-500 transition-colors text-slate-900 dark:text-white">
                    {p.title}
                  </div>
                  {p.product_score != null && (
                    <div className="mt-3 flex items-center justify-center">
                      <MiniScoreArc score={p.product_score} size={80} />
                    </div>
                  )}
                  <div className="mt-auto pt-3 flex items-baseline gap-2 justify-center">
                    <div className="font-display font-bold text-lg text-slate-900 dark:text-white">₹{(p.price || 0).toLocaleString("en-IN")}</div>
                    {p.compare_at_price && p.compare_at_price > (p.price || 0) && (
                      <div className="text-xs text-slate-400 dark:text-slate-500 line-through font-display">₹{p.compare_at_price.toLocaleString("en-IN")}</div>
                    )}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}

      {/* Convert-to-signup CTA */}
      <div className="mt-8 bg-gradient-to-br from-orange-50 to-orange-100 dark:from-slate-800 dark:to-slate-900 border border-orange-200 dark:border-slate-700 p-6 flex flex-wrap items-center justify-between gap-4">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-orange-600 dark:text-orange-400 font-display font-bold">
            Start your own shelf
          </div>
          <div className="font-serif-display font-bold text-lg text-slate-900 dark:text-white mt-0.5">
            Save the products <em>you</em> trust.
          </div>
          <p className="text-xs text-slate-600 dark:text-slate-400 mt-1 max-w-md">
            Sign up in 30 seconds. Heart any product and get a shareable shelf you can send to friends and family.
          </p>
        </div>
        <Link
          to="/signup?next=/wishlist"
          data-testid="shared-wishlist-signup-cta"
          className="inline-flex items-center gap-1.5 bg-orange-500 hover:bg-orange-600 text-white text-sm font-display font-semibold px-5 py-2.5 transition-colors"
        >
          Create your shelf <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </ShopShell>
  );
};

export default SharedWishlistPage;
